import {
    buildReducer,
    DistributionListMember,
    makeHubPersonaId,
    getUniqueEmailAddressesFromPersonas,
} from '../../../../shared';
import type { EmailToPDLsMapState } from '../../..';
import {
    emailToPDLsMapLoadCompletedAction,
    addAllPersonasToPDLClickedAction,
    removeAllPersonasFromPDLClickedAction,
} from '../../../../hub';
import {
    privateDLUpdateCompletedAction,
    privateDLCreateCompletedAction,
    privateDLAddMembersCompletedAction,
    privateDLRemoveMembersCompletedAction,
} from '../../../../privatedlcreateedit';

export const defaultState: EmailToPDLsMapState = {
    emailToPDLsMap: {},
    emailToAddedPDLPersonaIdsWaitingForUpdate: {},
    emailToRemovedPDLPersonaIdsWaitingForUpdate: {},
};

export default buildReducer<EmailToPDLsMapState>()
    .handleDataAction(emailToPDLsMapLoadCompletedAction, (state, action) => {
        const emailToPDLsMapWithHubPersonaIds = { ...action.payload.emailToPDLsMap };
        Object.keys(emailToPDLsMapWithHubPersonaIds).map(function (key) {
            emailToPDLsMapWithHubPersonaIds[key] = emailToPDLsMapWithHubPersonaIds[
                key
            ].map(personaId => makeHubPersonaId(personaId));
        });

        return {
            ...state,
            emailToPDLsMap: emailToPDLsMapWithHubPersonaIds,
        };
    })
    .handleDataAction(privateDLUpdateCompletedAction, (state, action) => {
        const updatedMembersEmails = getUpdatedMembersEmails(
            action.payload.oldMembers,
            action.payload.newMembers
        );

        const updatedEmailToPDLsMap = addAndRemoveEmailsToEmailToPDLsMap(
            updatedMembersEmails.addedMembersEmails,
            updatedMembersEmails.deletedMembersEmails,
            { ...state.emailToPDLsMap },
            action.payload.hubPersonaId
        );

        return {
            ...state,
            emailToPDLsMap: updatedEmailToPDLsMap,
        };
    })
    .handleDataAction(privateDLCreateCompletedAction, (state, action) => {
        const createdPDLPersonaId = action.payload.privateDL.hubPersonaId;
        const newMembersEmail = action.payload.privateDL.members.map(member => {
            if (member.isPdl) {
                return member.displayName;
            } else {
                return member.emailAddressWrapper.EmailAddress.toLowerCase();
            }
        });

        const updatedEmailToPDLsMap = addEmailsToEmailToPDLsMap(
            newMembersEmail,
            { ...state.emailToPDLsMap },
            createdPDLPersonaId
        );

        return {
            ...state,
            emailToPDLsMap: updatedEmailToPDLsMap,
        };
    })
    .handleDataAction(
        privateDLAddMembersCompletedAction,
        (state, action) => {
            const addedMembersEmails = action.payload.addedMembers
                .filter(member => !member.isPdl)
                .map(pdlMember => pdlMember.emailAddressWrapper.EmailAddress.toLowerCase());

            const updatedEmailToPDLsMap = addAndRemoveEmailsToEmailToPDLsMap(
                addedMembersEmails,
                [],
                { ...state.emailToPDLsMap },
                action.payload.hubPersonaId
            );

            const updatedEmailToAddedPDLsMap = removeEmailsFromEmailToPDLsMap(
                addedMembersEmails,
                { ...state.emailToAddedPDLPersonaIdsWaitingForUpdate },
                action.payload.hubPersonaId
            );

            return {
                ...state,
                emailToPDLsMap: updatedEmailToPDLsMap,
                emailToAddedPDLPersonaIdsWaitingForUpdate: updatedEmailToAddedPDLsMap,
            };
        },
        (state, action) => {
            const notAddedMembersEmails = action.payload.notAddedMembers
                .filter(member => !member.isPdl)
                .map(pdlMember => pdlMember.emailAddressWrapper.EmailAddress.toLowerCase());

            const updatedEmailToAddedPDLsMap = removeEmailsFromEmailToPDLsMap(
                notAddedMembersEmails,
                { ...state.emailToAddedPDLPersonaIdsWaitingForUpdate },
                action.payload.hubPersonaId
            );

            return {
                ...state,
                emailToAddedPDLPersonaIdsWaitingForUpdate: updatedEmailToAddedPDLsMap,
            };
        }
    )
    .handleDataAction(
        privateDLRemoveMembersCompletedAction,
        (state, action) => {
            const removedMembersEmails = action.payload.removedMembers
                .filter(member => !member.isPdl)
                .map(pdlMember => pdlMember.emailAddressWrapper.EmailAddress.toLowerCase());

            const updatedEmailToPDLsMap = addAndRemoveEmailsToEmailToPDLsMap(
                [],
                removedMembersEmails,
                { ...state.emailToPDLsMap },
                action.payload.hubPersonaId
            );

            const updatedEmailToRemovedPDLsMap = removeEmailsFromEmailToPDLsMap(
                removedMembersEmails,
                { ...state.emailToRemovedPDLPersonaIdsWaitingForUpdate },
                action.payload.hubPersonaId
            );

            return {
                ...state,
                emailToPDLsMap: updatedEmailToPDLsMap,
                emailToRemovedPDLPersonaIdsWaitingForUpdate: updatedEmailToRemovedPDLsMap,
            };
        },
        (state, action) => {
            const notRemovedMembersEmails = action.payload.notRemovedMembers
                .filter(member => !member.isPdl)
                .map(pdlMember => pdlMember.emailAddressWrapper.EmailAddress.toLowerCase());

            const updatedEmailToRemovedPDLsMap = removeEmailsFromEmailToPDLsMap(
                notRemovedMembersEmails,
                { ...state.emailToRemovedPDLPersonaIdsWaitingForUpdate },
                action.payload.hubPersonaId
            );

            return {
                ...state,
                emailToRemovedPDLPersonaIdsWaitingForUpdate: updatedEmailToRemovedPDLsMap,
            };
        }
    )
    .handle(addAllPersonasToPDLClickedAction, (state, action) => {
        const addedPersonasEmail = getUniqueEmailAddressesFromPersonas(action.payload.personas);
        const updatedEmailToAddedPDLsMap = addEmailsToEmailToPDLsMap(
            addedPersonasEmail,
            { ...state.emailToAddedPDLPersonaIdsWaitingForUpdate },
            action.payload.currentList.hubPersonaId
        );

        return {
            ...state,
            emailToAddedPDLPersonaIdsWaitingForUpdate: updatedEmailToAddedPDLsMap,
        };
    })
    .handle(removeAllPersonasFromPDLClickedAction, (state, action) => {
        const removedPersonasEmail = getUniqueEmailAddressesFromPersonas(action.payload.personas);
        const updatedEmailToRemovedPDLsMap = addEmailsToEmailToPDLsMap(
            removedPersonasEmail,
            { ...state.emailToRemovedPDLPersonaIdsWaitingForUpdate },
            action.payload.currentList.hubPersonaId
        );

        return {
            ...state,
            emailToRemovedPDLPersonaIdsWaitingForUpdate: updatedEmailToRemovedPDLsMap,
        };
    })
    .done(defaultState);

function addAndRemoveEmailsToEmailToPDLsMap(
    emailsToAdd: string[],
    emailsToDelete: string[],
    initEmailToPDLsMap: { [emailAddress: string]: string[] },
    hubPersonaId: string
): { [emailAddress: string]: string[] } {
    const afterAddEmailToPDLsMap = addEmailsToEmailToPDLsMap(
        emailsToAdd,
        initEmailToPDLsMap,
        hubPersonaId
    );
    return removeEmailsFromEmailToPDLsMap(emailsToDelete, afterAddEmailToPDLsMap, hubPersonaId);
}

function addEmailsToEmailToPDLsMap(
    emailsToAdd: string[],
    emailToPDLsMap: { [emailAddress: string]: string[] },
    hubPersonaId: string
): { [emailAddress: string]: string[] } {
    return emailsToAdd.reduce<{ [emailAddress: string]: string[] }>((mapping, addedEmail): {
        [emailAddress: string]: string[];
    } => {
        if (mapping[addedEmail]) {
            if (mapping[addedEmail].every(id => id != hubPersonaId)) {
                mapping[addedEmail] = [...mapping[addedEmail], hubPersonaId];
            }
            return mapping;
        } else {
            mapping[addedEmail] = [hubPersonaId];
            return mapping;
        }
    }, emailToPDLsMap);
}

function removeEmailsFromEmailToPDLsMap(
    emailsToDelete: string[],
    emailToPDLsMap: { [emailAddress: string]: string[] },
    hubPersonaId: string
): { [emailAddress: string]: string[] } {
    return emailsToDelete.reduce<{ [emailAddress: string]: string[] }>((mapping, deletedEmail): {
        [emailAddress: string]: string[];
    } => {
        if (mapping[deletedEmail]) {
            mapping[deletedEmail] = mapping[deletedEmail].filter(
                personaId => personaId !== hubPersonaId
            );
            if (mapping[deletedEmail].length === 0) {
                delete mapping[deletedEmail];
            }
        }
        return mapping;
    }, emailToPDLsMap);
}

function getUpdatedMembersEmails(
    oldMembers: DistributionListMember[],
    newMembers: DistributionListMember[]
): { addedMembersEmails: string[]; deletedMembersEmails: string[] } {
    const oldMembersPDLName = oldMembers.filter(member => member.isPdl).map(pdl => pdl.displayName);

    const oldMembersPDLMemberEmails = oldMembers
        .filter(member => !member.isPdl)
        .map(pdlMember => pdlMember.emailAddressWrapper.EmailAddress);

    const addedMembersEmails = newMembers
        .filter(
            member =>
                (!member.isPdl &&
                    !oldMembersPDLMemberEmails.includes(member.emailAddressWrapper.EmailAddress)) ||
                (member.isPdl && !oldMembersPDLName.includes(member.displayName))
        )
        .map(member => {
            if (member.isPdl) {
                return member.displayName;
            } else {
                return member.emailAddressWrapper.EmailAddress;
            }
        });

    const deletedMembersEmails = oldMembers
        .filter(
            member =>
                (!member.isPdl &&
                    !newMembers.some(
                        newMember =>
                            !newMember.isPdl &&
                            newMember.emailAddressWrapper.EmailAddress ===
                                member.emailAddressWrapper.EmailAddress
                    )) ||
                (member.isPdl &&
                    !newMembers.some(
                        newMember => newMember.isPdl && newMember.displayName === member.displayName
                    ))
        )
        .map(member => {
            if (member.isPdl) {
                return member.displayName;
            } else {
                return member.emailAddressWrapper.EmailAddress;
            }
        });

    return { addedMembersEmails: addedMembersEmails, deletedMembersEmails: deletedMembersEmails };
}
